import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

const ATTRIBUTE_KEY = 'Bake Day';
const BAKE_WEEKDAYS = [2, 5]; // Tuesday, Friday

const SLICED_TAG = 'sliced';
const WEDNESDAY_TAG = 'wednesday';

const TUESDAY_ONLY_WEEKDAYS = [2];
const WEDNESDAY_ONLY_WEEKDAYS = [3];

const CUTOFF_MINUTES = 9 * 60;
const WEEKDAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday',
  'Thursday', 'Friday', 'Saturday'
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Cache product tags to avoid unnecessary requests.
const productTagCache = new Map();

async function getProductTags(handle) {
  if (productTagCache.has(handle)) {
    return productTagCache.get(handle);
  }

  const response = await fetch(
    `${window.Shopify?.routes?.root || '/'}products/${handle}.js`
  );

  if (!response.ok) {
    throw new Error(`Failed to load product: ${handle}`);
  }

  const product = await response.json();

  const tags = Array.isArray(product.tags)
    ? product.tags.map(tag => String(tag).toLowerCase().trim())
    : String(product.tags || '')
        .split(',')
        .map(tag => tag.toLowerCase().trim());

  productTagCache.set(handle, tags);

  return tags;
}

async function getCartClassification() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    cache: 'no-store'
  });

  if (!response.ok) {
    throw new Error('Failed to load cart');
  }

  const cart = await response.json();

  const productTags = await Promise.all(
    cart.items.map(item => getProductTags(item.handle))
  );

  const hasBreakfast = productTags.some(tags =>
    tags.includes(WEDNESDAY_TAG)
  );

  const hasNormal = productTags.some(tags =>
    !tags.includes(WEDNESDAY_TAG)
  );

  const hasSliced = productTags.some(tags =>
    tags.includes(SLICED_TAG)
  );

  return {
    hasBreakfast,
    hasNormal,
    hasSliced,
    invalidCart: hasBreakfast && hasNormal
  };
}

// Shared validation state.
let breakfastCartInvalid = false;
let breakfastValidationPending = true;
let breakfastValidationVersion = 0;

// This prevents the bake-day selector from enabling checkout
// when the breakfast restriction is active.
function canEnableCheckout() {
  return !breakfastCartInvalid && !breakfastValidationPending;
}

function updateCheckoutButtons() {
  document.querySelectorAll('[name="checkout"]').forEach(button => {
    if (!canEnableCheckout()) {
      button.disabled = true;
      button.setAttribute('data-breakfast-blocked', 'true');
    } else {
      button.removeAttribute('data-breakfast-blocked');

      // Only enable checkout if a bake day has been selected.
      const selector = document.querySelector('bake-day-selector');

      if (selector?.currentValue) {
        button.disabled = false;
      }
    }
  });
}

function updateBreakfastWarnings() {
  document.querySelectorAll('.breakfast-cart-warning').forEach(el => {
    el.remove();
  });

  if (!breakfastCartInvalid) return;

  document.querySelectorAll('[name="checkout"]').forEach(button => {
    const warning = document.createElement('div');

    warning.className = 'breakfast-cart-warning';

    warning.textContent =
      'Breakfast boxes must be ordered separately. Please remove all other products from your basket to continue.';

    warning.style.cssText = `
      background: #fff1f0;
      color: #b91c1c;
      border: 1px solid #fca5a5;
      border-radius: 6px;
      padding: 12px 14px;
      margin: 12px 0;
      font-size: 14px;
      font-weight: 600;
      line-height: 1.5;
    `;

    button.parentElement?.insertBefore(warning, button);
  });
}

async function validateBreakfastCart() {
  const version = ++breakfastValidationVersion;

  breakfastValidationPending = true;
  updateCheckoutButtons();

  try {
    const classification = await getCartClassification();

    // Ignore outdated requests.
    if (version !== breakfastValidationVersion) return;

    breakfastCartInvalid = classification.invalidCart;
    breakfastValidationPending = false;

    updateCheckoutButtons();
    updateBreakfastWarnings();

  } catch (error) {
    if (version !== breakfastValidationVersion) return;

    console.error('Breakfast cart validation error:', error);

    // Keep checkout blocked if validation cannot complete.
    breakfastValidationPending = true;
    updateCheckoutButtons();
  }
}

class BakeDaySelector extends Component {

  restrictedWeekdays = BAKE_WEEKDAYS;

  connectedCallback() {
    super.connectedCallback?.();

    this.timezone =
      this.dataset.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Customer must actively select a bake day.
    this.currentValue = '';

    this.#applyBakeDayRestriction();

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#handleExternalRefresh
    );

    document.addEventListener(
      DialogOpenEvent.eventName,
      this.#handleExternalRefresh,
      { capture: true }
    );

    validateBreakfastCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleExternalRefresh
    );

    document.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleExternalRefresh,
      { capture: true }
    );
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#refresh();
  }

  #handleExternalRefresh = () => {
    requestAnimationFrame(() => this.#refresh());
  };

  #refresh() {
    this.#applyBakeDayRestriction();

    validateBreakfastCart();

    if (this.currentValue) {
      document.querySelectorAll('[name="checkout"]').forEach(btn => {
        if (canEnableCheckout()) {
          btn.removeAttribute('disabled');
        }
      });

      document
        .querySelectorAll('.cart__bake-day-required-note')
        .forEach(note => note.remove());
    }
  }

  async #applyBakeDayRestriction() {
    try {
      const classification = await getCartClassification();

      if (classification.hasBreakfast) {
        this.restrictedWeekdays = WEDNESDAY_ONLY_WEEKDAYS;
      } else if (classification.hasSliced) {
        this.restrictedWeekdays = TUESDAY_ONLY_WEEKDAYS;
      } else {
        this.restrictedWeekdays = BAKE_WEEKDAYS;
      }

    } catch (error) {
      console.error('Bake day restriction error:', error);
      this.restrictedWeekdays = BAKE_WEEKDAYS;
    }

    this.renderOptions();
  }

  getZonedNow() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(new Date());

    const map = {};

    for (const part of parts) {
      map[part.type] = part.value;
    }

    return {
      year: parseInt(map.year, 10),
      month: parseInt(map.month, 10),
      day: parseInt(map.day, 10),
      hour: map.hour === '24' ? 0 : parseInt(map.hour, 10),
      minute: parseInt(map.minute, 10)
    };
  }

  getAvailableBakeDays(count = 2) {
    const now = this.getZonedNow();

    const anchor = Date.UTC(
      now.year,
      now.month - 1,
      now.day
    );

    const nowMinutes = now.hour * 60 + now.minute;

    const results = [];

    for (let i = 1; i < 30 && results.length < count; i++) {
      const candidate = new Date(anchor + i * 86400000);

      const weekday = candidate.getUTCDay();

      if (!this.restrictedWeekdays.includes(weekday)) {
        continue;
      }

      const cutoffOffsetDays = i - 1;

      const available =
        cutoffOffsetDays > 0 ||
        (
          cutoffOffsetDays === 0 &&
          nowMinutes < CUTOFF_MINUTES
        );

      if (!available) continue;

      const label =
        `${WEEKDAY_NAMES[weekday]}, ` +
        `${MONTH_NAMES[candidate.getUTCMonth()]} ` +
        `${candidate.getUTCDate()}`;

      const value =
        `${WEEKDAY_NAMES[weekday]}, ` +
        `${candidate.getUTCFullYear()}-` +
        `${String(candidate.getUTCMonth() + 1).padStart(2, '0')}-` +
        `${String(candidate.getUTCDate()).padStart(2, '0')}`;

      results.push({ label, value });
    }

    return results;
  }

  getMissedBakeDay() {
    const now = this.getZonedNow();

    const anchor = Date.UTC(
      now.year,
      now.month - 1,
      now.day
    );

    const nowMinutes = now.hour * 60 + now.minute;

    const candidate = new Date(anchor + 86400000);

    const weekday = candidate.getUTCDay();

    if (
      !this.restrictedWeekdays.includes(weekday) ||
      nowMinutes < CUTOFF_MINUTES
    ) {
      return null;
    }

    return {
      weekday: WEEKDAY_NAMES[weekday],
      label:
        `${MONTH_NAMES[candidate.getUTCMonth()]} ` +
        `${candidate.getUTCDate()}`
    };
  }

  renderOptions() {
    const options = this.getAvailableBakeDays(2);

    this.#renderMissedNotice(this.getMissedBakeDay());

    const fieldset = this.querySelector(
      '[ref="bakeDayOptions"]'
    );

    if (!fieldset) return;

    const existingInputs = fieldset.querySelectorAll(
      'input[name="bake-day"]'
    );

    const alreadyRendered =
      existingInputs.length === options.length &&
      Array.from(existingInputs).every(
        (input, index) => input.value === options[index]?.value
      );

    // Clear a selected date if it is no longer valid.
    if (
      this.currentValue &&
      !options.some(option => option.value === this.currentValue)
    ) {
      this.currentValue = '';
      this.removeAttribute('data-current');

      document.querySelectorAll('[name="checkout"]').forEach(btn => {
        btn.disabled = true;
      });
    }

    if (alreadyRendered) {
      existingInputs.forEach(input => {
        input.checked = this.currentValue === input.value;
      });

      return;
    }

    fieldset.innerHTML = '';

    for (const option of options) {
      const id =
        `bake-day-${option.value.replace(/[^a-zA-Z0-9]/g, '-')}`;

      const wrapper = document.createElement('label');

      wrapper.className = 'bake-day-selector__option';
      wrapper.setAttribute('for', id);

      const input = document.createElement('input');

      input.type = 'radio';
      input.name = 'bake-day';
      input.id = id;
      input.value = option.value;
      input.checked = this.currentValue === option.value;

      input.addEventListener('change', () => {
        this.selectBakeDay(option.value);
      });

      const text = document.createElement('span');

      text.textContent = option.label;

      wrapper.appendChild(input);
      wrapper.appendChild(text);
      fieldset.appendChild(wrapper);
    }

    if (options.length === 0) {
      fieldset.innerHTML =
        '<p>No upcoming bake days are available right now. Please check back soon.</p>';
    }
  }

  #renderMissedNotice(missed) {
    const notice = this.querySelector(
      '[ref="bakeDayMissedNotice"]'
    );

    if (!notice) return;

    if (missed) {
      notice.textContent =
        `Missed ${missed.weekday}'s cut-off for ${missed.label}? ` +
        `We sometimes have spare stock — call us on ` +
        `07510 537803 to check.`;

      notice.hidden = false;
    } else {
      notice.textContent = '';
      notice.hidden = true;
    }
  }

  async selectBakeDay(value) {
    this.currentValue = value;

    this.setAttribute('data-current', value);

    const status = this.querySelector(
      '[ref="bakeDayStatus"]'
    );

    if (status) {
      status.textContent = 'Saving...';
    }

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            [ATTRIBUTE_KEY]: value
          }
        })
      });

      const response = await fetch(
        Theme.routes.cart_update_url,
        config
      );

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      if (status) {
        status.textContent =
          `Bake day set to ${value.split(', ')[0]}.`;
      }

      // Only enable checkout if breakfast cart validation passes.
      document.querySelectorAll('[name="checkout"]').forEach(button => {
        if (canEnableCheckout()) {
          button.removeAttribute('disabled');
        }
      });

      document
        .querySelectorAll('.cart__bake-day-required-note')
        .forEach(note => note.remove());

      document.querySelectorAll('bake-day-selector').forEach(element => {
        if (element !== this) {
          element.setAttribute('data-current', value);

          if (typeof element.refresh === 'function') {
            element.refresh();
          }
        }
      });

      validateBreakfastCart();

    } catch (error) {
      if (status) {
        status.textContent =
          'Something went wrong saving your bake day. Please try again.';
      }
    }
  }

  refresh() {
    this.#refresh();
  }
}

if (!customElements.get('bake-day-selector')) {
  customElements.define('bake-day-selector', BakeDaySelector);
}

// Recheck checkout restrictions after cart updates.
document.addEventListener(ThemeEvents.cartUpdate, () => {
  breakfastValidationPending = true;
  updateCheckoutButtons();

  requestAnimationFrame(() => {
    validateBreakfastCart();
  });
});

// Recheck when the cart drawer opens.
document.addEventListener(
  DialogOpenEvent.eventName,
  () => {
    requestAnimationFrame(() => {
      validateBreakfastCart();
    });
  },
  { capture: true }
);

// Initial validation.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    validateBreakfastCart();
  });
} else {
  validateBreakfastCart();
}

// Block normal checkout form submissions while the cart is invalid
// or its contents are still being checked.
document.addEventListener('submit', event => {
  const form = event.target;

  if (!(form instanceof HTMLFormElement)) return;

  const checkoutButton = form.querySelector('[name="checkout"]');

  if (!checkoutButton) return;

  if (!canEnableCheckout()) {
    event.preventDefault();
    event.stopImmediatePropagation();

    updateCheckoutButtons();
    updateBreakfastWarnings();
  }
}, true);